/**
 * Resolves entity_type + entity_id → frontend route URL for a notification click.
 * Returns null if the entity type is unknown — caller should fall back to mark-read-only
 * (not navigate).
 */

import { resolveServiceTableName } from './resolveServiceTableName';

type IdLike = string | number;

const encodeId = (id: IdLike): string => encodeURIComponent(String(id));

const MAP: Record<string, (id: IdLike) => string> = {
  tasks:             (id) => `/tasks?taskId=${encodeId(id)}`,
  leads:             (id) => `/leads/${encodeId(id)}`,
  tickets:           (id) => `/ticket/${encodeId(id)}`,        // singular route in AB3 — verified
  agreements:        (id) => `/agreements/${encodeId(id)}`,
  support:           (id) => `/support/${encodeId(id)}`,
  customers:         (id) => `/customers/${encodeId(id)}`,
  payment_schedules: (id) => `/payment-schedules/${encodeId(id)}`,
  user_strategies:   (id) => `/user-strategies/${encodeId(id)}`,
  // WABA-чат (D-OV-11): backend шлёт entity_type='waba_chats' с PK чата.
  // Note: instance_id в URL не указываем — WabaContext resolve'ит из URL/localStorage.
  // WabaLayout (T2) подхватит ?chatId query-param и откроет конкретный чат.
  waba_chats:        (id) => `/waba?chatId=${encodeId(id)}`,
  // Telegram-чат: аналогично waba_chats — TelegramPage читает ?chatId,
  // инстанс resolve'ится через TelegramContext.
  telegram_chats:    (id) => `/telegram?chatId=${encodeId(id)}`,
  // Compliance reversion (T7). NB: entity_id здесь = course_id (контракт backend
  // presentation_reattestation_required). Ведёт учащегося на страницу курса для
  // повторного прохождения stale-модуля.
  presentation_reattestation_required: (id) => `/academy/courses/${encodeId(id)}`,
};

export function resolveNotificationUrl(entity_type: string, entity_id: IdLike): string | null {
  const builder = MAP[entity_type];
  if (builder) return builder(entity_id);

  // Dynamic entity_service: `svc_*` prefix — resolve via service table name
  if (entity_type.startsWith('svc_')) {
    const table = resolveServiceTableName(entity_type);
    if (table) return `/entities/${encodeURIComponent(table)}/${encodeId(entity_id)}`;
  }

  console.warn(`[notification-routes] No route for entity_type: "${entity_type}"`);
  return null;
}
